import { Dispatch } from 'react'
import { useMutation, gql } from '@apollo/client'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { IErrorAction, IPanelLoadingAction } from 'checkout/containers/CheckoutStateReducer'
import {
  ORDER_PROPS,
  PROMOTION_PROPS,
  SHIPPING_ADDRESS_COUNT as shippingAddressCount,
} from 'checkout/hooks/useOrder'
import { IPromotion } from '../types/IPromotion'
import { IOrder } from '../types/IOrder'
import { isTimeoutError, timeoutMessage } from '../utils/timeout'
import { t } from 'localization'
import { useAuth } from 'shared/hooks/useAuth'

const TP = 'checkout.hooks.usePromotion'

interface IUsePromotionProps {
  dispatch: Dispatch<IErrorAction | IPanelLoadingAction>
  promotion?: IPromotion
}

export interface IActivatePromoProps {
  orderNumber: number
  code: string
  setFieldError: (field: string, errorMsg: string) => void
}

export interface IConnectPromoProps {
  orderNumber: number
  productPrice: number
}

interface IUsePromotion {
  activatePromotion(input: IActivatePromoProps): void
  removePromotion(orderNumber?: number): void
}

const ACTIVATE_PROMOTION = gql`
  mutation activatePromotion($orderNumber: Int!, $code: String!, $shippingAddressCount: Int!) {
    activatePromotion(orderNumber: $orderNumber, code: $code) {
      ...OrderProps
      ...PromotionProps
    }
  }
  ${ORDER_PROPS}
  ${PROMOTION_PROPS}
`

const REMOVE_PROMOTION = gql`
  mutation removePromotion($orderNumber: Int!, $shippingAddressCount: Int!) {
    removePromotion(orderNumber: $orderNumber) {
      ...OrderProps
      ...PromotionProps
    }
  }
  ${ORDER_PROPS}
  ${PROMOTION_PROPS}
`

const usePromotion = ({ dispatch, promotion }: IUsePromotionProps): IUsePromotion => {
  const [activatePromotionMutation] = useMutation(ACTIVATE_PROMOTION)
  const [removePromotionMutation] = useMutation(REMOVE_PROMOTION)
  const { logoutUser } = useAuth()

  const activatePromotion = async ({ orderNumber, code, setFieldError }: IActivatePromoProps) => {
    if (!orderNumber) {
      throw new Error(
        t(
          `${TP}.activatePromotionError`,
          'Error trying to activate a promo code with a missing order',
        ),
      )
    }
    dispatch({ type: 'promotionLoading' })
    try {
      const { data } = await activatePromotionMutation({
        variables: { orderNumber, code, shippingAddressCount },
      })
      dispatch({ type: 'promotionRequestSuccess', payload: data.activatePromotion })
      // this updatedOrder here is needed because the above dispatch is async
      // so the update to `promotion` through CheckoutContext cannot be guaranteed
      const updatedOrder = data.activatePromotion as IOrder
      sendTrackingEvent('CHECKOUT_ADD_PROMO_CODE_TAP', {
        promo_code_entered: code,
        promo_code_id: updatedOrder?.promotion?.id,
        promo_code_name: updatedOrder?.promotion?.title,
        promo_code_amount: updatedOrder?.promotion?.value,
      })
    } catch (error) {
      if (/promo/i.test(error.message)) {
        // promo related errors are displayed above the input
        // by calling setFieldError
        setFieldError('code', error.message)
        dispatch({ type: 'promotionLoading', payload: false })
      } else {
        // general connection errors display above the panel
        if (isTimeoutError(error)) {
          dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
          await logoutUser()
          return
        }
        dispatch({ type: 'promotionRequestFailure', payload: error.message })
      }
      sendTrackingEvent('CHECKOUT_ADD_PROMO_CODE_TAP', {
        error_message: error.message,
      })
    }
  }

  const removePromotion = async (orderNumber: number) => {
    if (!orderNumber) {
      throw new Error(
        t(`${TP}.removePromotionError`, 'Error trying to remove a promo code from a missing order'),
      )
    }
    dispatch({ type: 'promotionLoading' })
    sendTrackingEvent('CHECKOUT_REMOVE_PROMO_CODE_TAP', {
      promo_code_id: promotion?.id,
      promo_code_name: promotion?.title,
      promo_code_amount: promotion?.value,
    })
    try {
      const { data } = await removePromotionMutation({
        variables: { orderNumber, shippingAddressCount },
      })
      dispatch({ type: 'promotionRequestSuccess', payload: data.removePromotion })
    } catch (error) {
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      dispatch({ type: 'promotionRequestFailure', payload: error.message })
    }
  }

  return { activatePromotion, removePromotion }
}

export default usePromotion
